import type { Query, QueryClient } from '@tanstack/react-query'

// Every Summary / Approvals / plan / location query key starts with this prefix
// (POLICY_QUERY_PREFIX, APPROVAL_QUERY_PREFIX, …). People and Contributions use
// 'annual-benefit-planning' instead, so they are never touched here — EMS-11156 D8
// keeps their cache as-is.
const OPEN_ENROLLMENT_QUERY_PREFIX = 'open-enrollment'

// Queries owned by providers that sit above the tabs (they never unmount on a
// tab switch), so they are reset — cleared and, when on screen, refetched.
const ALWAYS_MOUNTED_QUERY_SCOPES: ReadonlyArray<string> = [
  'oep-policy',
  'oep-enrollment-periods',
]

function isCompanyQuery(query: Query, companyId: string): boolean {
  const [prefix, ...rest] = query.queryKey
  return prefix === OPEN_ENROLLMENT_QUERY_PREFIX && rest.includes(companyId)
}

function isAlwaysMountedQuery(query: Query): boolean {
  const scope = query.queryKey[1]
  return (
    typeof scope === 'string' && ALWAYS_MOUNTED_QUERY_SCOPES.includes(scope)
  )
}

/**
 * EMS-11156 (D8): makes the Open Enrollment page load fresh data for a company.
 *
 * - Removes the company's cached Open Enrollment queries that are not on
 *   screen, so the tab that mounts next starts empty — it shows its skeleton
 *   and fetches again instead of showing cached data.
 * - Resets the Summary policy + enrollment-period queries (mounted above the
 *   tabs), so they clear and refetch too.
 * - Leaves People and Contributions, and any query still on screen (e.g. the
 *   Customer Configuration file list after Apply), untouched.
 */
export async function resetOpenEnrollmentCompanyQueries(
  queryClient: QueryClient,
  companyId: string
): Promise<void> {
  queryClient.removeQueries({
    type: 'inactive',
    predicate: (query) =>
      isCompanyQuery(query, companyId) && !isAlwaysMountedQuery(query),
  })
  await queryClient.resetQueries({
    predicate: (query) =>
      isCompanyQuery(query, companyId) && isAlwaysMountedQuery(query),
  })
}
